
import { Resolvers } from '@apollo/client/core';

import { gql } from 'apollo-angular';
import { OrderBasic } from '../../models/order-basic';
import Utils from '@app/shared/utils';
import { FulFillmentMethods } from '../../models/order-item';

const OrderBasicFieldsFragment = gql`
  fragment OrderBasicFields on Order {
    orderId
    customerId
    orderDate
    insertedDate
    status
    msrpTotal
    grandTotal
    refundedTotal
    insertedByUserFullName
    itemsBasic @client
  }
`;

const OrderItemBasicFragment = gql`
  fragment OrderItemBasic on OrderItem {
    name
    sku
    orderItemId
    quantity
    refundedQuantity
    fulfillableQuantity
    fulfilledQuantity
    fulfillmentMethod
    hasDependencies
    group {
      name
    }
    createdSubscription {
      subscriptionId
      name
      start
      isSave
      retainedByUserFullName
    }
  }
`;

export const OrderBasicFragment = gql`
  fragment OrderBasic on Order {
    ...OrderBasicFields
    items {
      ...OrderItemBasic
    }
  }
  ${OrderBasicFieldsFragment}
  ${OrderItemBasicFragment}
`;

export const OrderBasicExcludeInternalItemsFragment = gql`
  fragment OrderBasic on Order {
    ...OrderBasicFields
    items(excludeStoreInternalItems: $excludeStoreInternalItems) {
      ...OrderItemBasic
    }
  }
  ${OrderBasicFieldsFragment}
  ${OrderItemBasicFragment}
`;

interface OrderBasicQuery extends OrderBasic {
  items: {
    name: string,
    sku: string,
    orderItemId: string,
    quantity: number,
    refundedQuantity: number,
    fulfillableQuantity: number;
    fulfilledQuantity: number;
    fulfillmentMethod: FulFillmentMethods;
    group: { name: string },
    createdSubscription?: CreatedSubscription;
    hasDependencies: boolean;
  }[];
}

export interface CreatedSubscription {
  subscriptionId: string;
  name: string;
  start: Date;
  isSave: boolean;
  retainedByUserFullName: string;
}

export interface OrderItemBasic {
  name: string;
  sku: string;
  createdSubscription?: CreatedSubscription;
  // TODO: should this be another type on orderbasic instead of blowing this up?
  orderItemId: string;
  quantity: number;
  refundedQuantity: number;
  fulfillableQuantity: number;
  fulfilledQuantity: number;
  fulfillmentMethod: FulFillmentMethods;
  hasDependencies: boolean;
}

export const OrderItemBasicResolver: Resolvers = {
  Order: {
    itemsBasic: (parent: OrderBasicQuery): OrderItemBasic[] => parent.items.reduce((itemGroups, item) => {
      const name = item.group ? item.group.name : item.name;
      const existingItem = itemGroups.find(i => i.name === name);
      if (!existingItem) {
        const itemBasic: OrderItemBasic = { name, ...item };
        if (item.createdSubscription) {
          const { subscriptionId, start, isSave, retainedByUserFullName } = item.createdSubscription;
          itemBasic.createdSubscription = {
            subscriptionId,
            name,
            start: Utils.utcDate(start.toString()),
            isSave,
            retainedByUserFullName
          };
        }
        itemGroups.push(itemBasic);
      } else if (!existingItem.createdSubscription && item.createdSubscription) {
        existingItem.createdSubscription = {
          subscriptionId: item.createdSubscription.subscriptionId,
          name: item.createdSubscription.name,
          start: Utils.utcDate(item.createdSubscription.start.toString()),
          isSave: item.createdSubscription.isSave,
          retainedByUserFullName: item.createdSubscription.retainedByUserFullName
        };
      }
      return itemGroups;
    }, [] as OrderItemBasic[])
  }
};
